// Ported from markup_parser/src/lib/layout.ts.
// Computes lanes, columns, and per-card placement for the FlowVisualization
// (see flow-ui-specification.md in functions/prompts/ for the rendering rules).

import type { ParsedFlow, Statement, TriggerExpr } from './flowModel';
import { computeFilteredStatementIds, computeDimmedStatementIds } from './flowBoundedContext';

export type LaneKind = 'human' | 'externalSystem' | 'context' | 'website';

export interface Lane {
  key: string;
  label: string;
  kind: LaneKind;
  ownerActorKey?: string;
  ownerActorName?: string;
}

export interface Placement {
  id: number;
  column: number;
  laneKey: string;
}

export interface WebsiteMarker {
  kind: 'sends' | 'receives';
  laneKey: string;
  column: number;
  actionId: number;
  eventId?: number;
}

export interface UiStateCard {
  laneKey: string;
  column: number;
  event: Statement;
  emitterId: number;
}

export interface LaidOutFlow {
  lanes: Lane[];
  columns: Map<number, number>;
  columnCount: number;
  placements: Map<number, Placement>;
  cardHeights: Map<number, number>;
  laneHeights: Map<string, number>;
  laneTops: Map<string, number>;
  uiStateCards: UiStateCard[];
  websiteMarkers: WebsiteMarker[];
  embeddedEventIds: Set<number>;
  hiddenEventIds: Set<number>;
  producerOf: Map<number, number>;
  // Statements that were intentionally filtered out by a bounded-context
  // selection. Empty when no filter is active. The canvas uses these
  // sets to apply opacity / hide nodes; the layout itself only includes
  // visible statements in `placements`.
  filteredOutStatementIds: Set<number>;
  dimmedStatementIds: Set<number>;
  selectedBoundedContext: string | null;
}

export interface LayoutOptions {
  // When set, restrict the layout to statements that participate in any
  // chain involving this bounded context. Lanes / columns are recomputed
  // against the filtered set so the canvas isn't padded with empty
  // space where unrelated lanes used to be.
  selectedBoundedContext?: string | null;
  // 083 step-flow: 'layered' assigns columns by following the `from` edges
  // (column = upstream's column + 1), so alternatives that branch from the
  // same upstream SHARE a column and stack vertically — a decision renders
  // as a fork, not a false sequence. Default 'grammar' keeps the original
  // action-per-column assignment for the discovery grammar, unchanged.
  columnMode?: 'grammar' | 'layered';
}

export const COLUMN_WIDTH = 220;
export const LANE_LABEL_WIDTH = 220;
export const CONTENT_START_X = LANE_LABEL_WIDTH + 24;
export const COLUMN_GAP = 40;

export const BASE_CARD_HEIGHT = 88;
export const CHILD_ROW_HEIGHT = 30;
export const CARD_FOOTER_PAD = 10;
export const LANE_PAD_Y = 20;
export const MIN_ACTOR_LANE_HEIGHT = 150;
export const WEBSITE_LANE_MIN_HEIGHT = 110;
export const WEBSITE_LANE_WITH_STATE_HEIGHT = 190;

export function actorLaneKey(st: Statement): string | null {
  if (!st.actor) return null;
  return `actor:${st.actor.kind}:${st.actor.name}`;
}

export function websiteLaneKey(actorKey: string, sys: string): string {
  return `web:${actorKey}:${sys}`;
}

export function collectEventRefs(expr: TriggerExpr, out: number[]): void {
  if ('ref' in expr) out.push(expr.ref);
  else {
    collectEventRefs(expr.left, out);
    collectEventRefs(expr.right, out);
  }
}

export function layoutFlow(flow: ParsedFlow, opts: LayoutOptions = {}): LaidOutFlow {
  // Apply BC filter (if any) by narrowing the flow we operate on. Every
  // downstream step (lanes, columns, placements, lane heights) then sees
  // only the visible statements, which means no padding for hidden
  // lanes and tighter columns.
  const selectedBc = opts.selectedBoundedContext || null;
  const visibleIds = selectedBc
    ? computeFilteredStatementIds(flow, selectedBc)
    : new Set(flow.statements.map((s) => s.id));
  const dimmedStatementIds = selectedBc
    ? computeDimmedStatementIds(flow, selectedBc)
    : new Set<number>();
  const filteredOutStatementIds = new Set<number>();
  if (selectedBc) {
    for (const s of flow.statements) {
      if (!visibleIds.has(s.id)) filteredOutStatementIds.add(s.id);
    }
  }
  const visibleStatements = selectedBc
    ? flow.statements.filter((s) => visibleIds.has(s.id))
    : flow.statements;

  const byId = new Map<number, Statement>();
  visibleStatements.forEach((s) => byId.set(s.id, s));

  const producerOf = new Map<number, number>();
  for (const s of visibleStatements) {
    for (const e of s.emits) {
      if (!producerOf.has(e)) producerOf.set(e, s.id);
    }
  }
  // Also accept the "natural" emission form: `(event) X :: from [N]` means
  // "event X was emitted by statement N". Equivalent to writing
  // `[N] ... => { [X] }` on the producer. Both forms convey the same fact;
  // accepting both keeps natural-language LLM output from breaking edge
  // drawing when reactions reference these events via `:: on`.
  for (const s of visibleStatements) {
    if (s.type !== 'event') continue;
    if (s.triggeredBy?.kind !== 'from') continue;
    if (producerOf.has(s.id)) continue;
    producerOf.set(s.id, s.triggeredBy.actionId);
  }

  // 1. Build lanes (humans → external systems → bounded contexts; each gets a Website sub-lane per via)
  // When a filter is active, skip actors whose statements are all filtered out.
  const visibleActorNames = new Set<string>();
  for (const s of visibleStatements) {
    if (s.actor) visibleActorNames.add(`${s.actor.kind}:${s.actor.name}`);
  }
  const includeActor = selectedBc
    ? (kind: string, name: string) => visibleActorNames.has(`${kind}:${name}`)
    : () => true;

  const humans = flow.actors.filter((a) => a.kind === 'human' && includeActor('human', a.name));
  const externals = flow.actors.filter((a) => a.kind === 'externalSystem' && includeActor('externalSystem', a.name));
  const contexts = flow.actors.filter((a) => a.kind === 'context' && includeActor('context', a.name));

  const viasByActor = new Map<string, string[]>();
  for (const s of visibleStatements) {
    if (s.type !== 'action' || !s.actor || !s.via) continue;
    const k = `actor:${s.actor.kind}:${s.actor.name}`;
    const list = viasByActor.get(k) ?? [];
    for (const sys of s.via.sys) {
      if (!list.includes(sys)) list.push(sys);
    }
    viasByActor.set(k, list);
  }

  // Group by section so the sidebar's section caps render once each
  // ("1. ACTORS" / "2. TOUCHPOINTS & APPS" / "3. BOUNDED CONTEXTS"):
  //   1. all humans + all external systems
  //   2. every website lane across every actor
  //   3. all bounded contexts
  // Column placements are driven by action id, not lane order, so the
  // vertical regroup doesn't break horizontal alignment of cards.
  const lanes: Lane[] = [];
  for (const a of humans) {
    lanes.push({ key: `actor:human:${a.name}`, label: a.name, kind: 'human' });
  }
  for (const a of externals) {
    lanes.push({ key: `actor:externalSystem:${a.name}`, label: a.name, kind: 'externalSystem' });
  }
  for (const a of [...humans, ...externals, ...contexts]) {
    const ownerKey = `actor:${a.kind}:${a.name}`;
    for (const sys of viasByActor.get(ownerKey) ?? []) {
      lanes.push({
        key: websiteLaneKey(ownerKey, sys),
        label: sys,
        kind: 'website',
        ownerActorKey: ownerKey,
        ownerActorName: a.name,
      });
    }
  }
  for (const a of contexts) {
    lanes.push({ key: `actor:context:${a.name}`, label: a.name, kind: 'context' });
  }

  // 2. Identify #ui events (hoisted to Website lane)
  const uiEventIds = new Set<number>();
  for (const s of visibleStatements) {
    if (s.type === 'event' && s.tags && s.tags['ui'] === true) {
      uiEventIds.add(s.id);
    }
  }

  // 3. Column assignment
  // Each action starts a NEW column at currentMax+1. Its handler and emitted events
  // share the action's column. Reactions :: on [n] occupy n.col + 1. Events emitted
  // by reactions share the reaction's column. Actions are processed in id order so
  // action N+1's column lands strictly to the right of any chain rooted at N.
  const col = new Map<number, number>();

  // 083 layered mode (the step-flow grammar): columns follow the `from`
  // edges — column = upstream's column + 1; no upstream = column 0. Steps
  // that branch from the same upstream share a column and stack, so a
  // decision's alternatives render as a fork. Unresolvable references
  // (cycles, filtered upstreams) degrade to column 0, never crash.
  if (opts.columnMode === 'layered') {
    const pending = [...visibleStatements];
    let progressed = true;
    while (pending.length > 0 && progressed) {
      progressed = false;
      for (let i = pending.length - 1; i >= 0; i -= 1) {
        const s = pending[i];
        const from = s.triggeredBy?.kind === 'from' ? s.triggeredBy.actionId : null;
        if (from === null || !byId.has(from)) {
          col.set(s.id, 0);
        } else if (col.has(from)) {
          col.set(s.id, col.get(from)! + 1);
        } else {
          continue;
        }
        pending.splice(i, 1);
        progressed = true;
      }
    }
    for (const s of pending) col.set(s.id, 0);
  }

  // In layered mode every statement already has its column; the grammar
  // assignment below must not run (its per-action `col.set` would overwrite).
  const actionsSorted = opts.columnMode === 'layered'
    ? []
    : [...visibleStatements]
      .filter((s) => s.type === 'action')
      .sort((a, b) => a.id - b.id);

  const currentMax = () => (col.size === 0 ? -1 : Math.max(...[...col.values()]));

  for (const action of actionsSorted) {
    col.set(action.id, currentMax() + 1);

    const queue: Statement[] = [action];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const s of visibleStatements) {
        if (col.has(s.id)) continue;

        if (s.triggeredBy?.kind === 'from' && s.triggeredBy.actionId === cur.id) {
          col.set(s.id, col.get(cur.id)!);
          queue.push(s);
          continue;
        }

        if (s.triggeredBy?.kind === 'on' && cur.type === 'event') {
          const refs: number[] = [];
          collectEventRefs(s.triggeredBy.expr, refs);
          if (!refs.includes(cur.id)) continue;
          if (!refs.every((r) => col.has(r))) continue;
          const maxRefCol = Math.max(...refs.map((r) => col.get(r)!));
          col.set(s.id, maxRefCol + 1);
          queue.push(s);
          continue;
        }

        if (s.type === 'event') {
          const prod = producerOf.get(s.id);
          if (prod === cur.id) {
            col.set(s.id, col.get(cur.id)!);
            queue.push(s);
          }
        }
      }
    }
  }

  // Anything unreachable from any action lands at the right edge.
  for (const s of visibleStatements) {
    if (!col.has(s.id)) col.set(s.id, currentMax() + 1);
  }

  const columnCount = Math.max(0, ...[...col.values()].map((c) => c + 1));

  // 4. Placement (which lane) and Website-lane synthetics
  const placements = new Map<number, Placement>();
  const embeddedEventIds = new Set<number>();
  const hiddenEventIds = new Set<number>();
  const uiStateCards: UiStateCard[] = [];
  const websiteMarkers: WebsiteMarker[] = [];

  for (const s of visibleStatements) {
    if (s.type === 'event') {
      if (uiEventIds.has(s.id)) {
        const producerId = producerOf.get(s.id);
        if (producerId !== undefined) {
          const producer = byId.get(producerId);
          let actionAncestor: Statement | null = null;
          if (producer?.triggeredBy?.kind === 'from') {
            actionAncestor = byId.get(producer.triggeredBy.actionId) ?? null;
          }
          if (actionAncestor?.actor && actionAncestor.via) {
            const ak = `actor:${actionAncestor.actor.kind}:${actionAncestor.actor.name}`;
            // Hoist the #ui state card onto every touchpoint lane the action
            // spans — the result is "displayed on X and Y" when a single
            // action runs through multiple venues.
            const matchingLaneKeys = actionAncestor.via.sys
              .map((sys) => websiteLaneKey(ak, sys))
              .filter((wkey) => lanes.find((l) => l.key === wkey));
            if (matchingLaneKeys.length > 0) {
              for (const wkey of matchingLaneKeys) {
                uiStateCards.push({
                  laneKey: wkey,
                  column: col.get(s.id)!,
                  event: s,
                  emitterId: producerId,
                });
              }
              // Placement carries a single laneKey; park the event on the first
              // touchpoint's lane so node lookups still work.
              placements.set(s.id, {
                id: s.id,
                column: col.get(s.id)!,
                laneKey: matchingLaneKeys[0],
              });
              hiddenEventIds.add(s.id);
              continue;
            }
          }
        }
      }
      embeddedEventIds.add(s.id);
      continue;
    }

    const laneKey = actorLaneKey(s);
    if (!laneKey) continue;
    placements.set(s.id, {
      id: s.id,
      column: col.get(s.id) ?? 0,
      laneKey,
    });
  }

  for (const s of visibleStatements) {
    if (s.type !== 'action' || !s.actor || !s.via) continue;
    const ak = `actor:${s.actor.kind}:${s.actor.name}`;
    const column = col.get(s.id)!;
    const handler = visibleStatements.find(
      (h) => h.triggeredBy?.kind === 'from' && h.triggeredBy.actionId === s.id,
    );
    const receivesEventId = handler && handler.type === 'command'
      ? handler.emits.find((e) => !uiEventIds.has(e)) ?? null
      : null;

    // Emit "sends" / "receives" markers for every touchpoint the action runs
    // through — one action spanning @Website + @MobileApp shows up on both lanes.
    for (const sys of s.via.sys) {
      const wkey = websiteLaneKey(ak, sys);
      if (!lanes.find((l) => l.key === wkey)) continue;
      websiteMarkers.push({ kind: 'sends', laneKey: wkey, column, actionId: s.id });
      // "receives" — only when the handler is a command emitting at least one
      // non-#ui event. If the handler emits only #ui events, the state card IS
      // the confirmation the user sees — don't duplicate it with a receives pill.
      if (receivesEventId !== null) {
        websiteMarkers.push({
          kind: 'receives',
          laneKey: wkey,
          column,
          actionId: s.id,
          eventId: receivesEventId,
        });
      }
    }
  }

  // 5. Card heights
  const cardHeights = new Map<number, number>();
  for (const s of visibleStatements) {
    if (s.type === 'event') continue;
    if (!placements.has(s.id)) continue;
    let h = BASE_CARD_HEIGHT;
    if (s.type === 'command' || s.type === 'reaction' || s.type === 'query') {
      const childCount = s.emits.filter(
        (eid) => !uiEventIds.has(eid) && embeddedEventIds.has(eid),
      ).length;
      if (childCount > 0) {
        h += CARD_FOOTER_PAD + childCount * CHILD_ROW_HEIGHT;
      }
    }
    const hasTagChips = Object.keys(s.tags ?? {}).length > 0;
    if (hasTagChips) h += 22;
    cardHeights.set(s.id, h);
  }

  // 6. Lane heights
  //
  // Cards in the same (lane, column) cell stack vertically with a 12px gap
  // (mirrors FlowCanvas's stackIdx * (cardH + 12) placement). Lane height
  // must be the tallest stack across all columns in the lane — using just
  // the tallest single card lets a many-reaction cell overflow into the
  // next lane and cards visually collide.
  const STACK_GAP_Y = 12;
  const laneHeights = new Map<string, number>();
  for (const lane of lanes) {
    if (lane.kind === 'website') {
      const hasState = uiStateCards.some((u) => u.laneKey === lane.key);
      laneHeights.set(
        lane.key,
        hasState ? WEBSITE_LANE_WITH_STATE_HEIGHT : WEBSITE_LANE_MIN_HEIGHT,
      );
    } else {
      // Group placements in this lane by column, accumulate the total
      // stack height per column, take the max.
      const columnStackHeights = new Map<number, number>();
      for (const [sid, p] of placements) {
        if (p.laneKey !== lane.key) continue;
        const stmt = byId.get(sid);
        if (!stmt || stmt.type === 'event') continue; // events don't render as cards
        const cardH = cardHeights.get(sid) ?? BASE_CARD_HEIGHT;
        const prev = columnStackHeights.get(p.column);
        // First card in the column = its own height; each subsequent card
        // adds gap + its own height.
        columnStackHeights.set(p.column, prev == null ? cardH : prev + STACK_GAP_Y + cardH);
      }
      let maxStack = MIN_ACTOR_LANE_HEIGHT - LANE_PAD_Y * 2;
      for (const h of columnStackHeights.values()) {
        if (h > maxStack) maxStack = h;
      }
      laneHeights.set(lane.key, maxStack + LANE_PAD_Y * 2);
    }
  }

  // 7. Lane tops (y positions)
  const laneTops = new Map<string, number>();
  let y = 0;
  for (const lane of lanes) {
    laneTops.set(lane.key, y);
    y += laneHeights.get(lane.key) ?? MIN_ACTOR_LANE_HEIGHT;
  }

  return {
    lanes,
    columns: col,
    columnCount,
    placements,
    cardHeights,
    laneHeights,
    laneTops,
    uiStateCards,
    websiteMarkers,
    embeddedEventIds,
    hiddenEventIds,
    producerOf,
    filteredOutStatementIds,
    dimmedStatementIds,
    selectedBoundedContext: selectedBc,
  };
}
